import FirstPageIcon from "@mui/icons-material/FirstPage";
import KeyboardArrowLeft from "@mui/icons-material/KeyboardArrowLeft";
import KeyboardArrowRight from "@mui/icons-material/KeyboardArrowRight";
import LastPageIcon from "@mui/icons-material/LastPage";
import { TableFooter, TablePagination } from "@mui/material";
import Box from "@mui/material/Box";
import IconButton from "@mui/material/IconButton";
import Paper from "@mui/material/Paper";
import { styled, useTheme } from "@mui/material/styles";
import Table from "@mui/material/Table";
import TableBody from "@mui/material/TableBody";
import TableCell, { tableCellClasses } from "@mui/material/TableCell";
import TableContainer from "@mui/material/TableContainer";
import TableHead from "@mui/material/TableHead";
import TableRow from "@mui/material/TableRow";
import * as React from "react";
import FlexPassApi from "../../../api/flexpassApi";
import MuiSwitch from "../../../components/MuiSwitch/MuiSwitch";
import { ISO639Lang } from "../../../constans/languagesList";
import { FlexPassSubscription } from "../../../types/FlexPassTypes";
import { formatDateAndTime } from "../../../utils/dateActions";
import { getTranslation } from "../../../utils/getTranslation";
import Notify from "../../../utils/toaster";
import { flexPassTypeStrings } from "../flexPassUtils";
import s from "./FlexPassTable.module.css";

interface TablePaginationActionsProps {
  count: number;
  page: number;
  rowsPerPage: number;
  onPageChange: (
    event: React.MouseEvent<HTMLButtonElement>,
    newPage: number
  ) => void;
}

function TablePaginationActions(props: TablePaginationActionsProps) {
  const theme = useTheme();
  const { count, page, rowsPerPage, onPageChange } = props;

  const handleFirstPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, 0);
  };

  const handleBackButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page - 1);
  };

  const handleNextButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, page + 1);
  };

  const handleLastPageButtonClick = (
    event: React.MouseEvent<HTMLButtonElement>
  ) => {
    onPageChange(event, Math.max(0, Math.ceil(count / rowsPerPage) - 1));
  };

  return (
    <Box sx={{ flexShrink: 0, ml: 2.5 }}>
      <IconButton
        onClick={handleFirstPageButtonClick}
        disabled={page === 0}
        aria-label="first page"
      >
        {theme.direction === "rtl" ? (
          <LastPageIcon color="success" />
        ) : (
          <FirstPageIcon color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleBackButtonClick}
        disabled={page === 0}
        aria-label="previous page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowRight color="success" />
        ) : (
          <KeyboardArrowLeft color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleNextButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="next page"
      >
        {theme.direction === "rtl" ? (
          <KeyboardArrowLeft color="success" />
        ) : (
          <KeyboardArrowRight color="success" />
        )}
      </IconButton>
      <IconButton
        onClick={handleLastPageButtonClick}
        disabled={page >= Math.ceil(count / rowsPerPage) - 1}
        aria-label="last page"
      >
        {theme.direction === "rtl" ? (
          <FirstPageIcon color="success" />
        ) : (
          <LastPageIcon color="success" />
        )}
      </IconButton>
    </Box>
  );
}

const StyledTableCell = styled(TableCell)(({ theme }) => ({
  [`&.${tableCellClasses.head}`]: {
    backgroundColor: "#313131",
    color: "white",
  },
  [`&.${tableCellClasses.body}`]: {
    fontSize: 14,
    color: "white",
  },
}));

const StyledTableRow = styled(TableRow)(({ theme }) => ({
  "&:nth-of-type(odd)": {
    backgroundColor: "#2E2E2E",
  },
  "&:nth-of-type(even)": {
    backgroundColor: "#242424",
    border: 0,
  },
  // hide last border
  "td, th": {
    border: 0,
  },
}));

type FlexPassTableProps = {
  rows: FlexPassSubscription[];
  page: number;
  rowsPerPage: number;
  setPage: (page: number) => void;
  setRowsPerPage: (limit: number) => void;
  setSelectedFlexPass: React.Dispatch<
    React.SetStateAction<FlexPassSubscription | undefined>
  >;
  setFlexPasses: React.Dispatch<React.SetStateAction<FlexPassSubscription[]>>;
  count: number;
};
export default function FlexPassTable({
  rows,
  page,
  rowsPerPage,
  setPage,
  setRowsPerPage,
  setSelectedFlexPass,
  setFlexPasses,
  count,
}: FlexPassTableProps) {
  const token = localStorage.getItem("token");
  const handleChangePage = (
    event: React.MouseEvent<HTMLButtonElement> | null,
    newPage: number
  ) => {
    setPage(newPage + 1);
  };

  const handleChangeRowsPerPage = (
    event: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
  };

  const changeActiveStatus = async (value: boolean, id: string) => {
    if (!token) return;
    setFlexPasses((prev) => {
      return prev.map((item) => {
        if (item._id === id) {
          return { ...item, isActive: value };
        }
        return item;
      });
    });
    const response = await FlexPassApi.updateFlexPassSubscription(token, {
      _id: id,
      isActive: value,
    });
    if (!response.status) {
      setFlexPasses((prev) => {
        return prev.map((item) => {
          if (item._id === id) {
            return { ...item, isActive: !value };
          }
          return item;
        });
      });
      if (response.message) {
        Notify(response.message);
      }
    }
  };

  return (
    <TableContainer
      component={Paper}
      sx={{
        minWidth: 700,
        width: "100%",
        borderRadius: "20px",
        backgroundColor: "transparent",
      }}
    >
      <Table
        sx={{ minWidth: 700, width: "100%" }}
        aria-label="customized table"
      >
        <TableHead className={s.tableHead}>
          <TableRow>
            <StyledTableCell align="left">Title</StyledTableCell>
            <StyledTableCell align="left">Specialization</StyledTableCell>
            <StyledTableCell align="left">Language</StyledTableCell>
            <StyledTableCell align="left">Level</StyledTableCell>
            <StyledTableCell align="left">Start Date/End Date</StyledTableCell>
            <StyledTableCell align="left">Cost($)</StyledTableCell>
            <StyledTableCell align="left">Duration (days)</StyledTableCell>
            <StyledTableCell align="left">Type</StyledTableCell>
            <StyledTableCell align="left">Invited/Confirmed</StyledTableCell>
            <StyledTableCell align="left">Is Active</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {rows.map((row) => (
            <StyledTableRow
              key={row._id}
              sx={{
                cursor: "pointer",
              }}
              onClick={() => setSelectedFlexPass(row)}
            >
              <StyledTableCell>
                {getTranslation(row.title, "en")}
              </StyledTableCell>
              <StyledTableCell>
                {getTranslation(row.mainSpecialization.labels, "en")}
              </StyledTableCell>
              <StyledTableCell>{ISO639Lang[row.language]}</StyledTableCell>
              <StyledTableCell>
                {getTranslation(row.level.title, "en")}
              </StyledTableCell>
              <StyledTableCell>
                {`${
                  formatDateAndTime(new Date(row.startDate), "en").formattedDate
                } / ${
                  formatDateAndTime(new Date(row.endDate), "en").formattedDate
                }`}
              </StyledTableCell>
              <StyledTableCell>{row.cost / 100}$</StyledTableCell>
              <StyledTableCell>{row.duration}</StyledTableCell>
              <StyledTableCell>{flexPassTypeStrings[row.type]}</StyledTableCell>
              <StyledTableCell>
                <div>
                  {
                    row.entryCodes.filter(
                      (item, index, self) =>
                        index ===
                        self.findIndex(
                          (t) =>
                            t.forSpecialistUser._id ===
                            item.forSpecialistUser._id
                        )
                    ).length
                  }
                  /{row.specialists?.length}
                </div>
              </StyledTableCell>
              <StyledTableCell>
                <div onClick={(e) => e.stopPropagation()}>
                  <MuiSwitch
                    checked={row.isActive}
                    setChecked={(value) => changeActiveStatus(value, row._id)}
                  />
                </div>
              </StyledTableCell>
            </StyledTableRow>
          ))}
        </TableBody>
        <TableFooter className={s.tableFooter}>
          <TableRow
            sx={{
              minWidth: 700,
              width: "100%",
              border: "none",
            }}
          >
            <TablePagination
              rowsPerPageOptions={[10, 20, 30]}
              colSpan={10}
              count={count}
              rowsPerPage={rowsPerPage}
              page={page - 1}
              slotProps={{
                select: {
                  inputProps: {
                    "aria-label": "rows per page",
                  },
                  native: true,
                },
              }}
              onPageChange={handleChangePage}
              onRowsPerPageChange={handleChangeRowsPerPage}
              ActionsComponent={TablePaginationActions}
              sx={{ border: "none", color: "white" }}
            />
          </TableRow>
        </TableFooter>
      </Table>
    </TableContainer>
  );
}
